/**
 * ParkingCount Value Object
 * 
 * Domain concept: "How many parking spots does this property have?"
 * 
 * Business rules:
 * - Null parking normalized to null (use hasValue to check)
 * - Zero parking is valid (property exists but has no parking)
 * 
 * Computed facts:
 * - hasValue: Whether parking data exists (not null)
 * - hasParking: Whether parking is available (value > 0)
 * 
 * Usage:
 * - Format: src/ui/parking/format-parking-count.ts
 * - UI: src/ui/stats/hero-stats.tsx
 */
export class ParkingCount {
  readonly #value: number | null;

  private constructor(value: number | null) {
    this.#value = value;
  }

  /**
   * Creates a ParkingCount from a numeric value.
   */
  static from(parking: number | null | undefined): ParkingCount {
    return new ParkingCount(parking ?? null);
  }

  /**
   * Returns the numeric value
   */
  get value(): number | null {
    return this.#value;
  }

  /**
   * Checks if parking data exists (not null)
   */
  get hasValue(): boolean {
    return this.#value !== null;
  }

  /**
   * Checks if parking is available (greater than 0)
   */
  get hasParking(): boolean {
    return this.#value !== null && this.#value > 0;
  }
}
